import { StrictMode, useMemo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ActionProvider, configureApp, createVixioClient, loadAudit, VixioError, type ActionEnv } from "@vixio/data";
import { AuditProvider, ToastProvider, useToast } from "@vixio/ui";
import "@vixio/ui/styles.css";
import "./app.css";
import { installBrowserReporting } from "@vixio/errors/browser";
import { env } from "./env";
import { App } from "./App";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { StepUpProvider, useStepUp } from "./auth/mfa";
import { AppChrome } from "./shell/AppChrome";

// Erros não tratados vão ao GlitchTip (sem dado pessoal; ver packages/errors). Antes de montar o app.
installBrowserReporting({
  dsn: env.sentryDsn,
  environment: env.envLabel || "production",
  tags: { app: "painel" },
});

createVixioClient(env.supabaseUrl, env.supabaseAnonKey);
configureApp({ meetUrl: env.meetUrl });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof VixioError && err.kind !== "network" && err.kind !== "unknown") && count < 1,
    },
  },
});

function ActionBridge({ children }: { children: ReactNode }) {
  const stepUp = useStepUp();
  const toast = useToast();
  const value = useMemo<ActionEnv>(
    () => ({
      stepUp,
      notify: (tone, title, body) => (tone === "ok" ? toast.ok(title, body) : toast.error(title, body)),
    }),
    [stepUp, toast],
  );
  return <ActionProvider value={value}>{children}</ActionProvider>;
}

function StepUpWithAuth({ children }: { children: ReactNode }) {
  const { refreshAal } = useAuth();
  return <StepUpProvider onElevated={refreshAal}>{children}</StepUpProvider>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppChrome />
      <ToastProvider>
        <AuthProvider>
          <StepUpWithAuth>
            <ActionBridge>
              <AuditProvider load={loadAudit}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </AuditProvider>
            </ActionBridge>
          </StepUpWithAuth>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
